import { useState } from 'react';
import Spinner from 'components/loader/Spinner';
import AutocompleteTooltip, {
    Option,
} from 'components/Inputs/InputsWithTooltip/AutocompleteTooltip';
import { NButton, NDialog, NInput } from '@innersource-nn/novoui-react';
import NCheckboxTooltip from 'components/Inputs/InputsWithTooltip/NCheckboxTooltip';
import NSelectTooltip from 'components/Inputs/InputsWithTooltip/NSelectTooltip';
import useListEnvironments from 'views/Datasets/hooks/useListEnvironments';
import { EnvironmentSelectItem } from 'types/Environment';
import useCreatePipeline from '../hooks/useCreatePipeline';
import { NewSqlPipelineInput } from 'types/Pipeline';
import { useNavigate } from 'react-router-dom';
import { ModalProps } from 'types/Modal';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';

const filter = {
    roles: ['Admin', 'Owner', 'Invited', 'DatasetCreator'],
    page: 1,
    pageSize: 20,
    onlyProduction: true,
    samlgroup: ['admin'],
};
const defaultValues = {
    label: '',
    environmentUri: '',
    tags: [],
    description: '',
    SamlGroupName: '',
    template: 'analytic',
    pipelineStageOptions: 'ALL',
};
export interface CreatePipelineProps extends ModalProps {
    title: string;
    type: string;
    loading?: boolean;
    onClick: VoidFunction;
}

const CreatePipeline = ({ visible, onClose }: ModalProps) => {
    const navigate = useNavigate();
    const [formData, setFormData] =
        useState<NewSqlPipelineInput>(defaultValues);

    const { environments, environmentError, rolesError, loading, roles } =
        useListEnvironments(formData.environmentUri, filter);

    const { buttonLoading, handleSubmit } = useCreatePipeline();
    const isButtonDisabled = () => {
        const { SamlGroupName, environmentUri, label, description } = formData;
        return (
            SamlGroupName === '' ||
            environmentUri === '' ||
            label === '' ||
            description === ''
        );
    };

    function handleSelectEnvironment(selectedEnvironment: Option) {
        //implement the logic to handle the selected environment as in handlechange
        const { key } = selectedEnvironment as EnvironmentSelectItem;
        setFormData({
            ...formData,
            environmentUri: key,
            SamlGroupName: '',
        });
    }

    const handleChange = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const { name, value } = event.target;
        if (name === 'environment') {
            const selectedEnvironment = environments.find(
                (option) => option.key === value,
            );

            if (selectedEnvironment) {
                setFormData({
                    ...formData,
                    environmentUri: selectedEnvironment.key,
                    SamlGroupName: '',
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    async function handleClick() {
        const newPipeline = await handleSubmit(formData);
        if (newPipeline) navigate(`/pipeline/${newPipeline.sqlPipelineUri}`);
    }

    return (
        <NDialog
            isVisible={visible}
            closeDialog={onClose}
            backgroundClose
            headline="Deploy Pipeline"
            size="medium"
            showDividers={false}
        >
            {loading ? (
                <Spinner />
            ) : (
                <div className="flex flex-col gap-16 mt-16">
                    <NInput
                        name="label"
                        label="Pipeline name*"
                        placeholder="Please insert a short title which describes this pipeline..."
                        variant="outline"
                        modelValue={formData.label}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                label: e.target.value,
                            })
                        }
                    />
                    <NTextAreaQuill
                        label="Pipeline description*"
                        variant="outline"
                        placeholder="Please insert a short description which describes what this pipeline contains and the purpose of the pipeline..."
                        modelValue={formData.description}
                        onChange={(value) =>
                            setFormData({
                                ...formData,
                                description: value,
                            })
                        }
                    />
                    <div className="grid grid-cols-2 gap-16 ">
                        <AutocompleteTooltip
                            label="Environment*"
                            placeholder="Search for environment...."
                            tooltip="Select the environment where the dataset will be deplayed in."
                            items={environments}
                            handleSelected={handleSelectEnvironment}
                            error={environmentError}
                        />
                        <div className="mt-8">
                            <NSelectTooltip
                                content="Select the admin group responsible for managing permissions and access controls."
                                label="Owner Group*"
                                size="medium"
                                variant="outline"
                                name="SamlGroupName"
                                options={roles}
                                disabled={
                                    (roles.length === 0 &&
                                        formData.SamlGroupName === '') ||
                                    environmentError !== ''
                                }
                                modelValue={formData.SamlGroupName}
                                onChange={handleChange}
                                placeholder="Select group"
                                invalid={rolesError !== ''}
                                assistiveText={rolesError}
                            />
                        </div>
                    </div>

                    <div className="flex justify-between mt-16">
                        <NCheckboxTooltip
                            content="Select to enable this pipeline to be deployed to all the accounts within the environment the pipeline belongs to and not only to production"
                            label="Deploy to all accounts"
                            name="pipelineStageOptions"
                            checkboxSize="medium"
                            checked={formData.pipelineStageOptions !== 'PROD'}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    pipelineStageOptions:
                                        formData.pipelineStageOptions === 'PROD'
                                            ? 'ALL'
                                            : 'PROD',
                                })
                            }
                        />
                        <NButton
                            variant="fill"
                            square
                            className="bg-sea-blue-600 mt-8"
                            onClick={handleClick}
                            disabled={isButtonDisabled()}
                            isLoading={buttonLoading}
                        >
                            Create Pipeline
                        </NButton>
                    </div>
                </div>
            )}
        </NDialog>
    );
};
export default CreatePipeline;
